import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IC_Assignment,
  IC_Dashboard,
  IC_DueRow,
  IC_Execution,
  IC_ExecutionDetail,
  IC_EvaluationResult,
  IC_HistoryPage,
  IC_Result,
  IC_RecalculationPreview,
  IC_Template,
  IC_TemplateSaveRequest
} from 'src/app/Interface/IC_Interface';

const BASE = `${environment.BaseUrl.replace(/\/+$/, '')}/Daro`;

@Injectable({ providedIn: 'root' })
export class ICService {
  constructor(private http: HttpClient) {}

  getTemplates(search?: string, status?: boolean): Observable<IC_Template[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (status !== undefined) params = params.set('status', String(status));
    return this.http.get<IC_Template[]>(`${BASE}/IC_Template/GetList`, { params });
  }

  getTemplate(id: number): Observable<IC_Template> {
    return this.http.get<IC_Template>(`${BASE}/IC_Template/Get`, { params: new HttpParams().set('id', id) });
  }

  saveTemplate(model: IC_TemplateSaveRequest): Observable<any> {
    return this.http.post(`${BASE}/IC_Template/Save`, model);
  }

  createTemplateVersion(id: number): Observable<any> {
    return this.http.post(`${BASE}/IC_Template/CreateVersion`, null, { params: new HttpParams().set('id', id) });
  }

  getAssignments(templateId?: number, equipmentId?: number, active?: boolean): Observable<IC_Assignment[]> {
    let params = new HttpParams();
    if (templateId) params = params.set('templateId', templateId);
    if (equipmentId) params = params.set('equipmentId', equipmentId);
    if (active !== undefined) params = params.set('active', String(active));
    return this.http.get<IC_Assignment[]>(`${BASE}/IC_Assignment/GetList`, { params });
  }

  saveAssignment(model: { iC_Template_ID: number; deviceBehfa_IDs: number[]; date_Start: string }): Observable<any> {
    return this.http.post(`${BASE}/IC_Assignment/Save`, model);
  }

  getAssignmentDetail(id: number): Observable<any> {
    return this.http.get(`${BASE}/IC_Assignment/GetDetail`, { params: new HttpParams().set('id', id) });
  }

  setAssignmentActive(id: number, active: boolean): Observable<any> {
    return this.http.post(`${BASE}/IC_Assignment/SetActive`, null, { params: new HttpParams().set('id', id).set('active', active) });
  }

  getDue(status?: string, equipmentId?: number, templateId?: number,
    filters: { groupId?: number; subGroupId?: number; templateTitle?: string; equipmentKeyword?: string; window?: string; dueStatus?: string; scopeWindow?: string; excludeBlocked?: boolean } = {}): Observable<IC_DueRow[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (equipmentId) params = params.set('equipmentId', equipmentId);
    if (templateId) params = params.set('templateId', templateId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<IC_DueRow[]>(`${BASE}/IC_Due/Get`, { params });
  }

  createExecution(model: { iC_Assignment_Item_ID: number; execution_Source: string; execution_Reason?: string; calculationVersion?: 'IC-2' }): Observable<{ id: number; execution: IC_Execution }> {
    return this.http.post<{ id: number; execution: IC_Execution }>(`${BASE}/IC_Execution/Create`, model);
  }

  getExecution(id: number): Observable<IC_ExecutionDetail> {
    return this.http.get<IC_ExecutionDetail>(`${BASE}/IC_Execution/Get`, { params: new HttpParams().set('id', id) });
  }

  getExecutionStartStates(assignmentId: number, assignmentItemId?: number): Observable<import('src/app/Interface/IC_Interface').IC_ExecutionStartState[]> {
    let params = new HttpParams().set('assignmentId', assignmentId);
    if (assignmentItemId !== undefined) params = params.set('assignmentItemId', assignmentItemId);
    return this.http.get<import('src/app/Interface/IC_Interface').IC_ExecutionStartState[]>(`${BASE}/IC_Execution/StartState`, { params });
  }

  exportExecutionPdf(id: number): Observable<Blob> {
    return this.http.get(`${BASE}/IC_Execution/ExportPdf`, { params: new HttpParams().set('id', id), responseType: 'blob' });
  }

  exportExecutionExcel(id: number): Observable<Blob> {
    return this.http.get(`${BASE}/IC_Execution/ExportExcel`, { params: new HttpParams().set('id', id), responseType: 'blob' });
  }

  recalculatePreview(id: number): Observable<IC_RecalculationPreview> {
    return this.http.get<IC_RecalculationPreview>(`${BASE}/IC_Execution/RecalculatePreview`, { params: new HttpParams().set('id', id) });
  }

  exportReviewPreview(id: number, format: 'pdf' | 'excel'): Observable<Blob> {
    return this.http.get(`${BASE}/IC_Execution/RecalculatePreview${format === 'pdf' ? 'Pdf' : 'Excel'}`,
      { params: new HttpParams().set('id', id), responseType: 'blob' });
  }

  saveResult(executionId: number, model: Omit<IC_Result, 'result'>): Observable<IC_EvaluationResult> {
    return this.http.post<IC_EvaluationResult>(`${BASE}/IC_Execution/Result/Save`, model, { params: new HttpParams().set('id', executionId) });
  }

  saveMeanResult(executionId: number, model: { iC_Template_Item_ID: number; comment: string;
    readings: { reading_No: number; exact_Value: string; unit: string }[] }): Observable<IC_EvaluationResult> {
    return this.http.post<IC_EvaluationResult>(`${BASE}/IC_Execution/Result/Save`, model, { params: new HttpParams().set('id', executionId) });
  }

  completeExecution(id: number, model: any): Observable<IC_Execution> {
    return this.http.post<IC_Execution>(`${BASE}/IC_Execution/Complete`, model, { params: new HttpParams().set('id', id) });
  }

  approveExecution(id: number, model: any): Observable<IC_Execution> {
    return this.http.post<IC_Execution>(`${BASE}/IC_Execution/Approve`, model, { params: new HttpParams().set('id', id) });
  }

  getHistory(filters: { equipmentId?: number; templateId?: number; result?: string; from?: string; to?: string }): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<any[]>(`${BASE}/IC_Execution/History`, { params });
  }

  getHistoryPage(filters: { [key: string]: string | number | boolean | undefined }): Observable<IC_HistoryPage> {
    let params = new HttpParams().set('paged', 'true');
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<IC_HistoryPage>(`${BASE}/IC_Execution/History`, { params });
  }

  getEquipmentHistory(equipmentId: number, itemId?: number): Observable<any[]> {
    let params = new HttpParams().set('equipmentId', equipmentId);
    if (itemId) params = params.set('itemId', itemId);
    return this.http.get<any[]>(`${BASE}/IC_Report/EquipmentHistory`, { params });
  }

  getTrend(equipmentId: number, itemId: number): Observable<any[]> {
    const params = new HttpParams().set('equipmentId', equipmentId).set('itemId', itemId);
    return this.http.get<any[]>(`${BASE}/IC_Report/Trend`, { params });
  }


  uploadAttachment(executionId: number, fileType: string, file: File): Observable<any> {
    const form = new FormData();
    form.append('executionId', String(executionId));
    form.append('fileType', fileType);
    form.append('file', file, file.name);
    return this.http.post(`${BASE}/IC_Attachment/Upload`, form);
  }

  getAttachments(executionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${BASE}/IC_Attachment/GetList`, { params: new HttpParams().set('executionId', executionId) });
  }

  downloadAttachment(id: number): Observable<Blob> {
    return this.http.get(`${BASE}/IC_Attachment/Download`, { params: new HttpParams().set('id', id), responseType: 'blob' });
  }

  deleteAttachment(id: number): Observable<any> {
    return this.http.delete(`${BASE}/IC_Attachment/Delete`, { params: new HttpParams().set('id', id) });
  }

  getDashboard(filters: { groupId?: number; equipmentKeyword?: string; templateId?: number; window?: string } = {}): Observable<IC_Dashboard> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<IC_Dashboard>(`${BASE}/IC_Report/Dashboard`, { params });
  }
}
